import React, { useContext } from "react";
import useSearch from "hooks/useSearch";
import { ReactComponent as SelectArrow } from "assets/svgs/select-arrow.svg";
import lockBodyScroll from "@helpers/lockBodyScroll";
import Icon from "@root/_components/Icon/icon";
import Link from "@root/helpers/link";
import { TELEPHONE_NUMBER } from "@root/utils/constants";
import { ProductFinderContext } from "@root/context/product-finder";

const Search = ({ search, handleSearchChange }) => (
    <div className="modal-dropdown__search">
        <div className="relative">
            <input
                type="text"
                value={search}
                onChange={(event) => handleSearchChange(event.target.value)}
                placeholder="Search..."
                className="modal-dropdown__search--input"
            />
            <svg className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
        </div>
    </div>
);

export const ModalDropdown = ({
    selected,
    options,
    optionProperty,
    onSelect,
    onGoBack,
    hasSearch,
    onOpen,
    showModal,
    disabled,
    className,
    dropdownsArr,
}) => {
    const { search, resetSearch, filteredOptions, handleSearchChange } = useSearch(options, optionProperty);
    const { productFinder, setProductFinder, errorField, setErrorField } = useContext(ProductFinderContext);

    const handleGoBack = (dropdownName) => {
        onGoBack(dropdownName);
        resetSearch();

        setErrorField(null);
    };

    const handleSelect = (dropdownName, selectedValue) => {
        onSelect(dropdownName, selectedValue);
        resetSearch();

        setErrorField(null);
    };


    lockBodyScroll(showModal, true);

    let steps = dropdownsArr?.map((item) => item.name);

    const currentStepIndex = steps?.findIndex((step) => step.toLowerCase() === optionProperty);

    const getStepValue = (stepName) => {
        const dropdownItem = dropdownsArr?.find((item) => item.name === stepName.toLowerCase());
        return dropdownItem?.selected || stepName;
    };

    return (
        <>
            <div
                role="button"
                tabIndex="-1"
                className={`finder-mobile__dropdown flex flex-col gap-2 ${disabled ? "opacity-70" : ""}`}
            >
                <div className={`flex flex-col md:py-2.5 py-1.5 px-2 w-full bg-white rounded-lg relative shadow-richTextBoxShadow z-100 ${className}`}>
                    <div
                        role="button"
                        tabIndex="-1"
                        onKeyDown={onOpen}
                        onClick={onOpen}
                        className="text-gray-701 text-left px-2 md:text-base text-base-xs"
                    >
                        {selected}
                    </div>
                    <SelectArrow className="absolute w-2.5 h-2.5 right-8 md:top-5 top-3" />
                </div>
            </div>

            {showModal && (
                <div className="w-full h-full min-h-screen min-w-screen fixed z-20 top-0 left-0 right-0 bg-white shadow-[0_100px_0_0_rgb(255,255,255)] z-[200]">
                    <div className="overflow-y-scroll h-full flex-col flex">
                        <div className="flex flex-col">
                            <div className="modal-dropdown__content">
                                <button type="button" onClick={() => handleGoBack(optionProperty)}>
                                    <Icon icon="chevron-left" size={30} color="#00344E" />
                                </button>
                                <img src="https://cdn.sanity.io/images/4swbd7aj/pre-prod/f97554c7af75237a5b89d6438daca9c756b0a303-240x92.png?w=240&h=92&auto=format" alt="Seal Skin Logo" />
                                <Link to={TELEPHONE_NUMBER} aria-label="Call Us" className="header__actions--phone">
                                    <Icon icon="phone-call" color="#00334e" size={24} />
                                </Link>
                            </div>
                            <div className="modal-dropdown__steps">
                                {steps?.map((step, index) => {
                                    const isCurrentStep = index === currentStepIndex;
                                    const stepValue = getStepValue(step);
                                    const isPreviousStep = index < currentStepIndex;

                                    return (
                                        <div
                                            key={step}
                                            className={`modal-dropdown__step w-32 capitalize flex flex-col justify-center items-center ${isCurrentStep ? "opacity-100" : isPreviousStep ? "opacity-75 cursor-pointer hover:opacity-100" : "opacity-50"}`}
                                            onClick={() => {
                                                if (isPreviousStep) {
                                                    handleGoBack(steps[index + 1].toLowerCase());
                                                }
                                            }}
                                            role="button"
                                            tabIndex={isPreviousStep ? 0 : -1}
                                            onKeyDown={(e) => {
                                                if (isPreviousStep && e.key === "Enter") {
                                                    handleGoBack(steps[index + 1].toLowerCase());
                                                }
                                            }}
                                        >
                                                <span className="modal-dropdown__steps-text">
                                                    {stepValue}
                                                </span>
                                            {isCurrentStep && (
                                                <div className="modal-dropdown__steps-current" />
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        {errorField && (
                            <div className="product-page-patio-summary__error">
                                <p className="product-page-patio-summary__error-text">
                                    Please select {errorField.toLowerCase() === "option" ? "an" : "a"} {errorField.toLowerCase()}
                                </p>
                            </div>
                        )}

                        {hasSearch && (
                            <Search
                                search={search}
                                handleSearchChange={handleSearchChange}
                            />
                        )}
                        <div className="flex flex-col">
                            {filteredOptions.map((option) => (
                                <button
                                    key={option[optionProperty]}
                                    type="button"
                                    className="flex justify-between items-center py-4 px-4 text-left text-base border-b border-gray-200 hover:bg-gray-50 modal-dropdown__option"
                                    onClick={() => handleSelect(optionProperty, option[optionProperty])}
                                >
                                    <span>{option[optionProperty]}</span>
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default ModalDropdown;
